import React from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn } from "../../helpers/cn";
import { MonthlyPoint, deltaPercent } from "../../helpers/dashboardMetrics";

interface StatTileProps {
  label: string;
  value: string;
  // Oldest-first. Drives both the sparkline and the month-over-month delta;
  // omit it for a figure that is already scoped to one period.
  series?: MonthlyPoint[];
  color?: string;
  caption?: string;
}

export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  series = [],
  color,
  caption,
}) => {
  const delta = deltaPercent(series);
  const rising = delta !== null && delta > 0;
  const falling = delta !== null && delta < 0;
  // Arrow plus a signed number, never colour alone.
  const DeltaIcon = rising ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="bg-white rounded-2xl shadow p-5 flex flex-col gap-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>

      <div className="text-3xl font-semibold text-black leading-none">{value}</div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-h-[1.25rem]">
          {delta !== null && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-sm font-medium",
                rising && "text-green-600",
                falling && "text-red-500",
                !rising && !falling && "text-gray-500"
              )}
            >
              {(rising || falling) && (
                <DeltaIcon className="w-4 h-4" aria-hidden="true" />
              )}
              {`${delta > 0 ? "+" : ""}${delta.toFixed(1)}%`}
            </span>
          )}
          {caption && <div className="text-xs text-gray-400">{caption}</div>}
        </div>

        {/* Two points draw a straight line that says nothing; measures with
            only a current and a previous value show the figure and its
            change instead. */}
        {series.length > 2 && (
          <div className="w-24 h-10 shrink-0" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
